import dynamic from "next/dynamic";
import { MarketingCenteredText } from "../../../components/MarketingCenteredTextBlock";
import { MarketingFooter } from "../../../components/MarketingFooter";
import { MarketingGetStarted } from "../../../components/MarketingGetStarted";
import { MarketingMenu } from "../../../components/MarketingMenu";
import { MarketingOGTags } from "../../../components/MarketingOGTags";
import { MarketingTextBlock } from "../../../components/MarketingTextBlock";
import { JournalistsDesktop, MarketingUseCaseHeader } from "../../../components/MarketingUseCaseHeader";
import { MarketingUserPledge } from "../../../components/MarketingUserPledge";
import { FadeUpContainer } from "../../../components/ReusableStyledComponents/FadeUp";
import { ScrollDownArrow } from "../../../components/ScrollDownArrow";
import { VerticalSpacing } from "../../../components/VerticalSpacing";
import { theme } from "../../../theme/theme";
import { APPLICATION_ROUTES } from "../../../shared-logic/routes";

const MarketingCarouselUseCases = dynamic(() => import("../../../components/MarketingCarouselUseCases/MarketingCarouselUseCases"), { ssr: false });

const urls = [
    "https://authory.com/BrianFung",
    "https://authory.com/AdamPowell",
    "https://authory.com/JacobKrol",
    "https://authory.com/MikeMurphy",
    "https://authory.com/BarbaraBalfour",
    "https://authory.com/ToddFitchette",
    "https://authory.com/StevenLevy",
    "https://authory.com/RonMiller",
    "https://authory.com/aisling",
    "https://authory.com/SeanMichaelKerner",
    "https://authory.com/AnneQuito",
    "https://authory.com/GlennFleishman",
    "https://authory.com/TheoFrancis"
];

export const JournalistsPageLayout = () => {
    return (
        <>
            <MarketingOGTags
                title="Authory – The all-in-one portfolio journalists are dreaming of"
                description="With Authory journalists have all their content in a single place, ready to supercharge their career."
                canonicalUrl={`${process.env.NEXT_PUBLIC_FRONTEND_URL}${APPLICATION_ROUTES.USE_CASE_JOURNALISTS}`}
            />
            <MarketingMenu />
            <FadeUpContainer>
                <ScrollDownArrow
                    content={
                        <VerticalSpacing top={0} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 20 }]}>
                            <MarketingUseCaseHeader
                                title="Journalists"
                                description={
                                    <>
                                        <strong>“I use Authory not only as a portfolio to showcase my work, but also as a file cabinet.</strong> I
                                        have 10+ years of automotive features posted and often use my older work for reference. With Authory, I can
                                        look up my published stories faster than I can find them on my computer.”
                                    </>
                                }
                                desktopImage={<JournalistsDesktop />}
                                mobileImage={<></>}
                                userBio={
                                    <>
                                        <strong>- Jim Koscs</strong>, Journalist
                                    </>
                                }
                                profileLink="https://authory.com/JimKoscs"
                            />
                        </VerticalSpacing>
                    }
                />
                <VerticalSpacing top={80}>
                    <MarketingCarouselUseCases
                        imagePath="/authory_static_files/images/use-cases/slider/journalists/journalists_"
                        urls={urls}
                        targetAudience="journalists"
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingCenteredText
                        title={"Portfolio"}
                        punchline={
                            <>
                                <strong>The all-in-one portfolio</strong> you've been dreaming of
                            </>
                        }
                        description={
                            <>
                                <p>
                                    Authory gives you a portfolio page that is close to magical: It is automatically updating with your new content
                                    whenever you publish something. It works with any type of content, from articles to podcasts, pictures or videos
                                    that you publish. And it automatically contains all your past pieces too, regardless of when and where they've
                                    been published.{" "}
                                </p>
                                <p>
                                    Authory is only portfolio page that works like an interactive resume showing the entire breadth and depth of your
                                    body of work. All while making sure your content looks beautiful right out of the box. There is no better way to
                                    make your work truly shine.
                                </p>
                            </>
                        }
                        href={APPLICATION_ROUTES.PORTFOLIO}
                        linkText={
                            <>
                                Learn more about <strong>Portfolio</strong>
                            </>
                        }
                        desktopIcon={<img src="/authory_static_files/images/icons/present_xl_active.svg" />}
                        mobileIcon={<img src="/authory_static_files/images/icons/present_m_active.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingCenteredText
                        title={"Archive"}
                        iconPosition="right"
                        punchline={
                            <>
                                <strong>Protect your work</strong> to protect your career
                            </>
                        }
                        description={
                            <>
                                <p>
                                    Most journalists have been burned in the past already: No matter if it's articles, podcasts, radio programs,
                                    videos and whatever else you as a journalist are creating, suddenly it's not available anymore where it used to
                                    be.{" "}
                                </p>
                                <p>
                                    A lot of sweat, blood and tears and eventually the result is gone and you have nothing to show for it. That's why
                                    creating copies of your work is paramount. And Authory does that for you: You'll get a backup of everything you've
                                    ever created in the past, and your new pieces will be backed up automatically too. For your complete peace of
                                    mind.
                                </p>
                            </>
                        }
                        href={APPLICATION_ROUTES.ARCHIVE}
                        linkText={
                            <>
                                Learn more about <strong>Archive</strong>
                            </>
                        }
                        desktopIcon={<img src="/authory_static_files/images/icons/protect_xl_active.svg" />}
                        mobileIcon={<img src="/authory_static_files/images/icons/protect_m_active.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingUserPledge
                        title={
                            <>
                                “Authory provides lots of peace of mind. My decision to sign up literally{" "}
                                <strong>saved years of my career from disappearing.</strong>”
                            </>
                        }
                        description={
                            <>
                                <strong>Ivan Fernandez,</strong> KCAT, LA Weekly and more
                            </>
                        }
                        avatarImage={"/authory_static_files/images/pledges/Ivan.jpg"}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingCenteredText
                        title={"Portfolio"}
                        punchline={
                            <>
                                <strong>Bulletproof confirmation</strong> where you've published
                            </>
                        }
                        description={
                            <>
                                <p>
                                    Your portfolio is not just proof of what you've done so far. It also shows who you've worked for as a journalist.
                                    And that in itself can be a huge accomplishment.
                                </p>
                                <p>
                                    {" "}
                                    With Authory, this proof of publication will be yours forever: If your pieces have been deleted at the original
                                    source, you can make them accessible again to the world via your Authory page, whether it's articles, videos or
                                    podcasts. It just takes single click in your Authory account to restore your proof of publication.
                                </p>
                            </>
                        }
                        href={APPLICATION_ROUTES.PORTFOLIO}
                        linkText={
                            <>
                                Learn more about <strong>Portfolio</strong>
                            </>
                        }
                        desktopIcon={<img src="/authory_static_files/images/icons/protect_xl_active.svg" />}
                        mobileIcon={<img src="/authory_static_files/images/icons/protect_m_active.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingCenteredText
                        title={"Archive"}
                        iconPosition="right"
                        punchline={
                            <>
                                <strong>Zero effort and zero tech skills</strong> required
                            </>
                        }
                        description={
                            <>
                                <p>
                                    Setting up your Authory account takes about 3 minutes. And with that you don't need to do anything anymore.
                                    Authory will automatically find all your past and your future work.
                                </p>
                                <p>
                                    Whether you create podcasts, or videos, or like to include content you post on social media, Authory will find and
                                    import it all for you. Or you can upload pieces yourself, e.g. PDFs from your computer. It could not be easier to
                                    finally track and have all your content under one roof without having to worry about the upkeep.
                                </p>
                            </>
                        }
                        href={APPLICATION_ROUTES.ARCHIVE}
                        linkText={
                            <>
                                Learn more about <strong>Archive</strong>
                            </>
                        }
                        desktopIcon={<img src="/authory_static_files/images/icons/track_xl_active.svg" />}
                        mobileIcon={<img src="/authory_static_files/images/icons/track_m_active.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingCenteredText
                        title={"Newsletter"}
                        punchline={
                            <>
                                Turn a casual audience into <strong>loyal subscribers</strong>
                            </>
                        }
                        description={
                            <>
                                <p>
                                    On your Authory page, your audience can subscribe to your email list with a single click, no signup necessary.
                                    It's usually a pain to find the right mailing provider, build and host a signup form and, most importantly,
                                    regularly create a newsletter.{" "}
                                </p>
                                <p>
                                    Authory does all that for you and automatically sends your subscribers a well-designed email digest with your new
                                    pieces on a daily or weekly basis. That's a direct relationship with your readers and literally zero effort for
                                    you.
                                </p>
                            </>
                        }
                        href={APPLICATION_ROUTES.NEWSLETTER_MARKETING}
                        linkText={
                            <>
                                Learn more about <strong>Newsletter</strong>
                            </>
                        }
                        desktopIcon={<img src="/authory_static_files/images/icons/distribute_xl_active.svg" />}
                        mobileIcon={<img src="/authory_static_files/images/icons/distribute_m_active.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingCenteredText
                        title={"Analytics"}
                        iconPosition="right"
                        punchline={
                            <>
                                Find out how your content <strong>performs on social media</strong>
                            </>
                        }
                        description={
                            <>
                                <p>
                                    There are plenty of ways to find out what your social media accounts' reach is. Yet, do you know what reach your
                                    content has, independently from your accounts? Authory will tell you exactly that: Regardless who shares, likes,
                                    comments, tweets or pins any content you've created , with Authory you'll know.
                                </p>
                                <p>
                                    You can check out your aggregate metrics, or you can drill them down and find out about the social media
                                    performance of individual content pieces anytime. And because Authory doesn't only monitor social media for your
                                    new pieces, but for your entire archive, you'll also know if older pieces of yours start trending again.
                                </p>
                            </>
                        }
                        href={APPLICATION_ROUTES.ANALYTICS_MARKETING}
                        linkText={
                            <>
                                Learn more about <strong>Analytics</strong>
                            </>
                        }
                        desktopIcon={<img src="/authory_static_files/images/icons/analyze_xl_active.svg" />}
                        mobileIcon={<img src="/authory_static_files/images/icons/analyze_m_active.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingCenteredText
                        title={"Collections"}
                        punchline={
                            <>
                                <strong>Turn your content into more</strong> than the sum of its parts
                            </>
                        }
                        description={
                            <>
                                <p>
                                    With Authory you'll have all your content from all around the web in a single place. But that is no one-way
                                    street: You can curate your pieces into Collections, mix and match articles with videos, pictures or podcasts.{" "}
                                </p>
                                <p>
                                    With Authory's automated rules this is done in no time and thanks to easy sharing options, you'll be able to share
                                    your Collections with exactly the people who are supposed to see them (or even keeping them entirely to yourself
                                    if you want to use them for your personal curation purposes only). In any case, it has never been easier to curate
                                    and share your body of work.
                                </p>
                            </>
                        }
                        href={APPLICATION_ROUTES.COLLECTIONS_MARKETING}
                        linkText={
                            <>
                                Learn more about <strong>Collections</strong>
                            </>
                        }
                        desktopIcon={<img src="/authory_static_files/images/icons/curate_xl_active.svg" />}
                        mobileIcon={<img src="/authory_static_files/images/icons/curate_m_active.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingTextBlock
                        title={
                            <>
                                Your very own <strong>blog included</strong>
                            </>
                        }
                        description={
                            <>
                                <p>
                                    Sometimes you might not want to write somewhere else. You might want to publish on your own turf instead. Forget
                                    about having to set up a blog for this, or having to use a third party service with zero control like Medium.
                                </p>
                                <p>
                                    Authory comes with a built-in blogging feature, so you can publish your articles, pictures or videos right on your
                                    Authory page. Of course, out of the box it'll look as brilliant as everything else on your Authory page does.
                                </p>
                            </>
                        }
                        icon={<img src="/authory_static_files/images/icons/blog.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120}>
                    <MarketingGetStarted />
                </VerticalSpacing>
                <VerticalSpacing top={120}>
                    <MarketingFooter />
                </VerticalSpacing>
            </FadeUpContainer>
        </>
    );
};
